/**
 * SYNCED FROM SERVER — do not edit by hand.
 *
 * Source: `server/src/api/<domain>/<domain>.contract.ts` (or the
 * shared bases under `server/src/api/common/contracts/`).
 *
 * Regenerate: `cd api-client && bun run sync:contracts`.
 *
 * The server is the source of truth for contract definitions. This
 * file exists so the web SPA can import zod schemas + oRPC contract
 * types from `@event-manager/api-client/contracts/<domain>.contract`
 * without reaching into the server workspace.
 */

import { z } from "zod";

export const WaitlistEntryStatusSchema = z.enum([
	"ACTIVE",
	"OFFERED",
	"FULFILLED",
	"CANCELLED",
	"LAPSED",
]);
export type WaitlistEntryStatus = z.infer<typeof WaitlistEntryStatusSchema>;

export const SlotOfferStatusSchema = z.enum(["OPEN", "CLOSED"]);
export type SlotOfferStatus = z.infer<typeof SlotOfferStatusSchema>;

export const SlotOfferTerminalReasonSchema = z.enum([
	"capacity_consumed",
	"expired",
	"disabled",
	"no_candidates",
]);
export type SlotOfferTerminalReason = z.infer<
	typeof SlotOfferTerminalReasonSchema
>;

export const SlotOfferCandidateStatusSchema = z.enum([
	"PENDING",
	"ENQUEUED",
	"SENDING",
	"SENT",
	"CLAIMED",
	"LOST",
	"LAPSED",
	"FAILED",
	"BLOCKED",
]);
export type SlotOfferCandidateStatus = z.infer<
	typeof SlotOfferCandidateStatusSchema
>;

const IsoDateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
const LocalTimeSchema = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/);

export const WaitlistDesiredWhenSchema = z.discriminatedUnion("kind", [
	z.object({ kind: z.literal("slot"), slotStart: z.iso.datetime() }),
	z
		.object({
			kind: z.literal("window"),
			dateStart: IsoDateSchema,
			dateEnd: IsoDateSchema,
			timeStart: LocalTimeSchema.optional(),
			timeEnd: LocalTimeSchema.optional(),
			weekdays: z.array(z.number().int().min(0).max(6)).max(7).optional(),
		})
		.refine((value) => value.dateStart <= value.dateEnd, {
			message: "dateStart must be before or equal to dateEnd",
		})
		.refine(
			(value) =>
				!(value.timeStart && value.timeEnd) || value.timeStart < value.timeEnd,
			{ message: "timeStart must be before timeEnd" }
		),
]);
export type WaitlistDesiredWhen = z.infer<typeof WaitlistDesiredWhenSchema>;

export const WaitlistEntrySchema = z.object({
	id: z.bigint(),
	orgId: z.string().min(1),
	contactId: z.string().min(1),
	eventTypeId: z.string().min(1),
	status: WaitlistEntryStatusSchema,
	desiredWhen: WaitlistDesiredWhenSchema,
	desiredUntil: z.date(),
	source: z.enum(["agent", "local"]),
	offeredCount: z.number().int().nonnegative(),
	note: z.string().trim().max(1000).nullable(),
	fulfilledEventId: z.bigint().nullable(),
	createdAt: z.date(),
	updatedAt: z.date(),
});
export type WaitlistEntry = z.infer<typeof WaitlistEntrySchema>;

export const SlotOfferSchema = z
	.object({
		id: z.bigint(),
		orgId: z.string().min(1),
		eventTypeId: z.string().min(1),
		slotStart: z.date(),
		status: SlotOfferStatusSchema,
		terminalReason: SlotOfferTerminalReasonSchema.nullable(),
		round: z.number().int().positive(),
		claimLimit: z.number().int().positive(),
		claimsTaken: z.number().int().nonnegative(),
		expiresAt: z.date(),
		trigger: z.enum(["cancellation", "modification", "horizon_open", "manual"]),
		createdAt: z.date(),
		updatedAt: z.date(),
	})
	.refine((offer) => offer.claimsTaken <= offer.claimLimit, {
		message: "claimsTaken cannot exceed claimLimit",
	});
export type SlotOffer = z.infer<typeof SlotOfferSchema>;

export const WaitlistOfferJobSchema = z.object({
	orgId: z.string().min(1),
	offerId: z.string().regex(/^\d+$/),
	candidateId: z.string().regex(/^\d+$/),
});
export type WaitlistOfferJob = z.infer<typeof WaitlistOfferJobSchema>;
